/**
 * Lumiera Hero Slideshow
 * Fade transitions, autoplay, swipe, keyboard, pause on hover
 */
(() => {
  class LumieraHeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.current = 0;
      this.timer = null;
      this.touchStartX = 0;
      this.touchDeltaX = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._bound = false;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (!this.slides.length) return;

      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.speed = Number(this.dataset.speed || 5000);
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.loop = this.dataset.loop !== 'false';

      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.prevBtn = this.querySelector('[data-hero-prev]');
      this.nextBtn = this.querySelector('[data-hero-next]');

      this.bindEvents();
      this.goTo(0, false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this._bound = false;
    }

    bindEvents() {
      if (this._bound) return;
      this._bound = true;

      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.prev();
        if (event.key === 'ArrowRight') this.next();
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });
      }

      this.addEventListener(
        'touchstart',
        (event) => {
          this.touchStartX = event.changedTouches[0].screenX;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (event) => {
          this.touchDeltaX = event.changedTouches[0].screenX - this.touchStartX;
          if (Math.abs(this.touchDeltaX) < 40) return;
          if (this.touchDeltaX > 0) this.prev();
          else this.next();
        },
        { passive: true }
      );

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }

    goTo(index, announce = true) {
      const total = this.slides.length;
      if (!total) return;

      let nextIndex = index;
      if (index < 0) nextIndex = this.loop ? total - 1 : 0;
      if (index >= total) nextIndex = this.loop ? 0 : total - 1;

      this.slides.forEach((slide, i) => {
        const active = i === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) slide.removeAttribute('tabindex');
        else slide.setAttribute('tabindex', '-1');
      });

      this.dots.forEach((dot, i) => {
        const active = i === nextIndex;
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.current = nextIndex;

      if (announce) {
        this.setAttribute('aria-live', 'polite');
      }
    }

    next() {
      this.goTo(this.current + 1);
      this.restartAutoplay();
    }

    prev() {
      this.goTo(this.current - 1);
      this.restartAutoplay();
    }

    startAutoplay() {
      if (!this.autoplay || this.slides.length < 2) return;
      this.stopAutoplay();
      this.timer = window.setInterval(() => this.next(), this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  if (!customElements.get('lumiera-hero-slideshow')) {
    customElements.define('lumiera-hero-slideshow', LumieraHeroSlideshow);
  }

  const initFadeUps = () => {
    const nodes = document.querySelectorAll('.lumiera-fade-up:not([data-observed])');
    if (!nodes.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach((node) => {
      node.setAttribute('data-observed', 'true');
      observer.observe(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFadeUps);
  } else {
    initFadeUps();
  }

  document.addEventListener('shopify:section:load', initFadeUps);
})();
